/// <amd-module name="Wit/Common/KanbanBoardSettings/Components/BoardSettingsViews/SwimlanesSettingsView" />
import * as React from "react";
import { paletteToHexColor } from "TFS/Samples/ThemeGenerator/ColorUtil";
import { IObservableArray, ObservableArray, ObservableValue, useDerivedObservable, useObservable, useSubscription } from "VSS/Core/Observable";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { useService, usePageContext } from "VSS/Platform/Layout";
import { IVssThemeService } from "VSS/Platform/Theming";
import { Button } from "VSSUI/Button";
import { Checkbox } from "VSSUI/Checkbox";
import { ColorDropdown } from "VSSUI/Components/ColorPicker/ColorDropdown";
import { FormItem } from "VSSUI/FormItem";
import { Header, TitleSize } from "VSSUI/Header";
import { IconSize } from "VSSUI/Icon";
import { ListDragDropBehavior, ListDragImage } from "VSSUI/List";
import { MenuButton } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { ITableColumn, SimpleTableCell, Table, TableRowRenderer } from "VSSUI/Table";
import { TextField } from "VSSUI/TextField";
import { testForHexString } from "VSSUI/Utilities/Color";
import { DragDropEffect } from "VSSUI/Utilities/DragDrop";
import { BoardRow } from "Wit/BoardsHub/Common/Client/Contracts/Settings";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { CoreFieldRefNames, FieldType } from "Wit/Common/Generated/Constants";
import { ISwimlanesSettings } from "Wit/Common/KanbanBoardSettings/Components/BoardSettingsViews/SwimlanesSettings";
import * as Resources from "Wit/Common/KanbanBoardSettings/Resources";
import { ISettingsView } from "Wit/Common/WitSettingsPanelTypes/Components/WitSettingsPanel.props";
import { getFieldCriteriaAsFilterClauses } from "Wit/Common/WitSettingsViews/Components/CriteriaTable/CriteriaService";
import { CriteriaTable } from "Wit/Common/WitSettingsViews/Components/CriteriaTable/CriteriaTable";
import { CriteriaTableModel, ICriteriaTableModel } from "Wit/Common/WitSettingsViews/Components/CriteriaTable/CriteriaTableModel";
import { TableStatus } from "Wit/Common/WitSettingsViews/Components/TableStatus";
import { IWorkItemFieldService } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService";
import { IWorkItemFieldService2 } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService2";

export function getSwimlanesSettingsTab(
    swimlanesSettings: ISwimlanesSettings,
    disabled: boolean,
    groupId: string,
    onSaveSettings?: () => Promise<void>
): ISettingsView {
    return {
        groupId,
        isDirty: swimlanesSettings.isDirty,
        isValid: swimlanesSettings.isValid,
        name: "Swimlanes",
        onSaveSettings,
        renderContent: () => <SwimlanesSettingsView disabled={disabled} settings={swimlanesSettings} />
    };
}
interface ISwimlanesSettingsViewProps {
    disabled?: boolean;
    settings: ISwimlanesSettings;
}

interface ISwimlaneListItem {
    swimlane: BoardRow;
    index: number;
    settings: ISwimlanesSettings;
    disabled?: boolean;
    expanded: boolean;
    setExpanded: (value: boolean) => void;
    isTableItem: boolean;
    renderRow?: TableRowRenderer<ISwimlaneListItem>;
}

type ExpandedSwimlaneSettings = { [rowId: BoardRow["id"]]: boolean | undefined };

const maxNumberOfSwimlaneWithRules = 20;

export function SwimlanesSettingsView({ disabled, settings }: ISwimlanesSettingsViewProps): JSX.Element {
    const columns = useSwimlaneColumns();
    const [expandedSettings, setExpandedSettings] = useObservable<ExpandedSwimlaneSettings>({});
    const numberOfSwimlanesWithRules = useDerivedObservable(
        settings.swimlanesRules,
        swimlaneRules => {
            return Object.entries(swimlaneRules).filter(([, rules]) => rules && rules.filterClauses.length > 0).length;
        },
        [settings]
    );

    const swimlanesToSwimlaneListItems = React.useCallback(
        (swimlanes: BoardRow[]) => {
            const swimlaneTableItems: ISwimlaneListItem[] = [];
            for (const [index, swimlane] of swimlanes.entries()) {
                const swimlaneTableItem = {
                    swimlane,
                    index,
                    settings,
                    disabled,
                    expanded: expandedSettings.value[swimlane.id] ?? false,
                    setExpanded: (value: boolean) => {
                        setExpandedSettings({ ...expandedSettings.value, [swimlane.id]: value });
                    }
                };
                swimlaneTableItems.push({ ...swimlaneTableItem, isTableItem: false });
                if (swimlaneTableItem.expanded) {
                    swimlaneTableItems.push({
                        ...swimlaneTableItem,
                        renderRow: (index, item, details): JSX.Element => {
                            return (
                                <tr className="swimlane-expanded-settings" key={item.swimlane.id + "details"}>
                                    <td aria-hidden="true" role="presentation" />
                                    <td className="padding-bottom-16" colSpan={columns.length}>
                                        <Observer numberOfSwimlanesWithRules={numberOfSwimlanesWithRules}>
                                            {({ numberOfSwimlanesWithRules }) => {
                                                return item.swimlane.isDefault ? (
                                                    <FormItem className="margin-horizontal-20" message={Resources.SwimlaneDefaultNoRules} />
                                                ) : numberOfSwimlanesWithRules >= maxNumberOfSwimlaneWithRules &&
                                                  settings.getRules(item.swimlane.id).filterClauses.length === 0 ? (
                                                    <FormItem className="margin-horizontal-20" message={Resources.SwimlaneMaxRules} />
                                                ) : (
                                                    <SwimlaneRulesTable listItem={item} settings={settings} disabled={disabled} />
                                                );
                                            }}
                                        </Observer>
                                    </td>
                                    <td aria-hidden="true" role="presentation" />
                                </tr>
                            );
                        },
                        isTableItem: true
                    });
                }
            }
            return swimlaneTableItems;
        },
        [settings, disabled, columns, expandedSettings, setExpandedSettings]
    );
    const swimlaneListItems = React.useMemo(() => {
        return new ObservableArray<ISwimlaneListItem>(swimlanesToSwimlaneListItems(settings.swimlanes.value));
    }, [settings, swimlanesToSwimlaneListItems]);

    useSubscription(
        expandedSettings,
        expandedSettings => {
            swimlaneListItems.splice(0, swimlaneListItems.length, ...swimlanesToSwimlaneListItems(settings.swimlanes.value));
        },
        [settings, swimlanesToSwimlaneListItems]
    );

    useSubscription(
        settings.swimlanes as IObservableArray<BoardRow>,
        swimlanes => {
            swimlaneListItems.splice(0, swimlaneListItems.length, ...swimlanesToSwimlaneListItems(swimlanes));
        },
        [settings, swimlanesToSwimlaneListItems]
    );

    const dragDropBehavior = React.useMemo(() => {
        return new ListDragDropBehavior<ISwimlaneListItem>({
            allowedTypes: ["swimlane"],
            onDragStart: event => {
                // don't drag table rows
                if (event.detail.dataTransfer.data.renderRow || event.detail.dataTransfer.type !== "swimlane") {
                    event.detail.dataTransfer.effectAllowed = DragDropEffect.none;
                }
            },
            onDragOver: event => {
                // don't drop to table rows
                if (event.detail.dataTransfer.data.renderRow || event.detail.dataTransfer.type !== "swimlane") {
                    event.detail.dataTransfer.effectAllowed = DragDropEffect.none;
                }
            },
            onDrop: (event, dropData) => {
                const targetRowIndex = dropData.index;
                const tableRowsBeforeTarget = swimlaneListItems.value.filter((item, index) => item.isTableItem && index < targetRowIndex).length;
                const isBeforeTargetRow = swimlaneListItems.value.findIndex(item => item === event.detail.dataTransfer.data) < targetRowIndex;
                const targetIndex = targetRowIndex - tableRowsBeforeTarget - (isBeforeTargetRow ? 1 : 0);
                settings.setOrder(event.detail.dataTransfer.data.swimlane.id, targetIndex);
            },
            renderDragImage: event => {
                const swimlane = event.detail.dataTransfer.data.swimlane;
                let swimlaneDisplayName = swimlane.name;
                if (!swimlaneDisplayName && settings.isDefault(swimlane.id)) {
                    swimlaneDisplayName = Resources.DefaultSwimlaneName;
                }
                return <ListDragImage text={swimlaneDisplayName} />;
            },
            type: "swimlane"
        });
    }, [settings, swimlaneListItems]);

    return (
        <div className="swimlane-settings">
            <Header
                className="padding-vertical-0"
                title={Resources.SwimlanesSettingsPanelTitle}
                titleSize={TitleSize.Small}
                description={Resources.SwimlanesSettingsPanelSubtitle}
            />
            <>
                <div className="margin-top-8 padding-horizontal-20">
                    <Button
                        disabled={disabled}
                        iconProps={{ iconName: "Add" }}
                        onClick={() => settings.addSwimlane(0)}
                        text={Resources.AddSwimlaneButtonText}
                    />
                </div>
                <Table
                    columns={columns}
                    className="swimlanes-settings-table"
                    showHeader
                    ariaLabel={Resources.SwimlanesSettingsPanelTitle}
                    behaviors={disabled ? undefined : [dragDropBehavior]}
                    itemProvider={swimlaneListItems}
                    selectRowOnClick={true}
                />
            </>
        </div>
    );
}

function useSwimlaneColumns(): ITableColumn<ISwimlaneListItem>[] {
    return React.useMemo(() => {
        return [
            {
                id: "swimlaneName",
                name: Resources.SwimlaneColumnLabel,
                renderCell: (rowIndex, columnIndex, column, { swimlane, settings, disabled, expanded, setExpanded }) => {
                    return (
                        <SimpleTableCell columnIndex={columnIndex} tableColumn={column}>
                            <div className="flex-row">
                                <div className="margin-right-8">
                                    <Button
                                        ariaLabel={expanded ? Resources.CollapseRow : Resources.ExpandRow}
                                        iconProps={{
                                            iconName: expanded ? "ChevronDown" : "ChevronRight",
                                            size: IconSize.small
                                        }}
                                        onClick={e => {
                                            e.preventDefault();
                                            setExpanded(!expanded);
                                        }}
                                        subtle
                                    />
                                </div>

                                <TextField
                                    containerClassName="name-text-field"
                                    disabled={disabled}
                                    onChange={(e, value) => settings.setName(swimlane.id, value)}
                                    value={swimlane.name}
                                    placeholder={settings.isDefault(swimlane.id) ? Resources.DefaultSwimlaneName : undefined}
                                    ariaLabel={Resources.SwimlaneColumnLabel}
                                />
                            </div>
                        </SimpleTableCell>
                    );
                },
                width: new ObservableValue(-37)
            },
            {
                id: "color",
                name: Resources.SwimlaneColorColumnLabel,
                renderCell: (rowIndex, columnIndex, column, { settings, swimlane, disabled }) => (
                    <SimpleTableCell columnIndex={columnIndex} tableColumn={column}>
                        <SwimlaneColorPicker swimlane={swimlane} settings={settings} disabled={disabled} />
                    </SimpleTableCell>
                ),
                width: new ObservableValue(-25)
            },
            {
                id: "moveButtons",
                name: Resources.SwimlaneMoveColumnLabel,
                renderCell: (rowIndex, columnIndex, column, { settings, index, swimlane, disabled }) => (
                    <SimpleTableCell columnIndex={columnIndex} tableColumn={column}>
                        <div className="flex-row move-buttons">
                            <Button
                                disabled={disabled || index < 1}
                                ariaLabel={Resources.SwimlaneContextMenuMoveUp}
                                iconProps={{ iconName: "ChevronUp" }}
                                subtle
                                onClick={() => {
                                    settings.setOrder(swimlane.id, index - 1);
                                }}
                            />
                            <Button
                                disabled={disabled || index >= settings.swimlanes.length - 1}
                                ariaLabel={Resources.SwimlaneContextMenuMoveDown}
                                iconProps={{ iconName: "ChevronDown" }}
                                subtle
                                onClick={() => {
                                    settings.setOrder(swimlane.id, index + 1);
                                }}
                            />
                            <MenuButton
                                iconProps={{ iconName: "MoreVertical" }}
                                ariaLabel={Resources.SwimlaneMoreMoveOptions}
                                subtle
                                disabled={disabled}
                                contextualMenuProps={{
                                    menuProps: {
                                        id: `columns-settings-swimlane-menu-${swimlane.id}`,
                                        items: [
                                            {
                                                id: `insert-above-${swimlane.id}`,
                                                text: Resources.SwimlaneContextMenuInsertAbove,
                                                onActivate: () => {
                                                    settings.addSwimlane(index);
                                                }
                                            },
                                            {
                                                id: `insert-below-${swimlane.id}`,
                                                text: Resources.SwimlaneContextMenuInsertBelow,
                                                onActivate: () => {
                                                    settings.addSwimlane(index + 1);
                                                }
                                            }
                                        ]
                                    }
                                }}
                            />
                        </div>
                    </SimpleTableCell>
                ),
                width: new ObservableValue(-25)
            },
            {
                id: "isDefault",
                name: Resources.SwimlaneIsDefaultColumnLabel,
                renderCell: (rowIndex, columnIndex, column, { settings, swimlane, disabled }) => (
                    <SimpleTableCell columnIndex={columnIndex} tableColumn={column}>
                        <div className="margin-left-8">
                            <Checkbox checked={settings.isDefault(swimlane.id)} disabled ariaLabel={Resources.SwimlaneIsDefaultColumnLabel} />
                        </div>
                    </SimpleTableCell>
                ),
                width: new ObservableValue(125)
            },
            {
                id: "delete",
                renderCell: (rowIndex, columnIndex, column, { settings, swimlane, disabled }) => (
                    <SimpleTableCell columnIndex={columnIndex} tableColumn={column}>
                        {!swimlane.isDefault && (
                            <Button
                                ariaLabel={Resources.SwimlaneContextMenuRemove}
                                className="bolt-list-cell-content-reveal padding-horizontal-4"
                                disabled={disabled}
                                iconProps={{ iconName: "Delete" }}
                                onClick={() => settings.removeSwimlane(swimlane.id)}
                                subtle={true}
                            />
                        )}
                    </SimpleTableCell>
                ),
                width: new ObservableValue(46)
            }
        ];
    }, []);
}
interface ISwimlaneWithRulesListItemProps {
    disabled?: boolean;
    listItem: ISwimlaneListItem;
    settings: ISwimlanesSettings;
    columns: ITableColumn<ISwimlaneListItem>[];
}

function SwimlaneColorPicker({
    swimlane,
    settings,
    disabled
}: {
    swimlane: BoardRow;
    settings: ISwimlanesSettings;
    disabled?: boolean;
}): JSX.Element {
    const themeService = useService<IVssThemeService>("IVssThemeService");
    const theme = themeService.getCurrentTheme();
    const memorizedColor = React.useMemo(() => {
        if (swimlane.color && testForHexString(swimlane.color)) {
            return swimlane.color;
        }

        return paletteToHexColor(theme.data["palette-primary-tint-30"])!;
    }, [swimlane.color]);
    return (
        <div className="flex-row flex-center full-width">
            <div className="margin-right-4 flex-grow">
                <ColorDropdown
                    ariaLabel={Resources.ColorColumnLabel}
                    onColorSelected={(color: string) => settings.setColor(swimlane.id, color)}
                    color={memorizedColor}
                    disabled={disabled}
                />
            </div>

            <Button
                ariaLabel={Resources.ResetToDefaultColorButtonLabel}
                className="reset-swimlane-color-button flex-baseline"
                disabled={swimlane.color === null || swimlane.color === undefined || disabled}
                iconProps={{ iconName: "Refresh" }}
                onClick={() => settings.setColor(swimlane.id, null)}
            />
        </div>
    );
}

const notSupportedFieldReferenceNames = new Set([
    CoreFieldRefNames.Id.toLowerCase(),
    CoreFieldRefNames.Title.toLowerCase(),
    CoreFieldRefNames.AreaId.toLowerCase(),
    CoreFieldRefNames.IterationId.toLowerCase(),
    CoreFieldRefNames.Rev.toLowerCase(),
    CoreFieldRefNames.RevisedDate.toLowerCase(),
    CoreFieldRefNames.NodeName.toLowerCase(),
    CoreFieldRefNames.TeamProject.toLowerCase(),
    CoreFieldRefNames.Watermark.toLowerCase(),
    CoreFieldRefNames.ExternalLinkCount.toLowerCase(),
    CoreFieldRefNames.HyperLinkCount.toLowerCase(),
    CoreFieldRefNames.RelatedLinkCount.toLowerCase(),
    CoreFieldRefNames.AttachedFileCount.toLowerCase(),
    CoreFieldRefNames.AuthorizedAs.toLowerCase(),
    CoreFieldRefNames.AuthorizedDate.toLowerCase(),
    CoreFieldRefNames.LinkType.toLowerCase()
]);

const supportedFieldTypes = new Set([
    FieldType.String,
    FieldType.Integer,
    FieldType.PlainText,
    FieldType.Double,
    FieldType.Boolean,
    FieldType.TreePath
]);

function SwimlaneRulesTable({
    disabled,
    settings,
    listItem
}: Pick<ISwimlaneWithRulesListItemProps, "disabled" | "settings" | "listItem">): JSX.Element {
    const { swimlane } = listItem;
    const errorMessage = settings.getSwimlaneNameError(swimlane.id);
    const hasError = useDerivedObservable(errorMessage, e => !!e, []);
    const pageContext = usePageContext();

    const workItemFieldsService = isFeatureFlagEnabled(pageContext, FeatureFlags.UseNewWorkItemFieldService, false) 
      ? useService<IWorkItemFieldService2>("IWorkItemFieldService2")
      : useService<IWorkItemFieldService>("IWorkItemFieldService");

    const criteriaTableModel = React.useMemo<ICriteriaTableModel>(() => {
        return new CriteriaTableModel(
            workItemFieldsService,
            notSupportedFieldReferenceNames,
            supportedFieldTypes,
            settings.getRules(swimlane.id).filterClauses.map(filter => {
                return {
                    field: filter.fieldName,
                    operator: filter.operator,
                    criteriaValue: new ObservableValue(filter.value)
                };
            }),
            criteria =>
                settings.setRules(swimlane.id, {
                    rowId: swimlane.id,
                    filterClauses: getFieldCriteriaAsFilterClauses(criteria)
                })
        );
    }, [swimlane.id]);

    React.useEffect(() => {
        settings.setCriteriaErrorObservable(swimlane.id, criteriaTableModel.validationErrorMessage);
    }, [swimlane.id, criteriaTableModel.validationErrorMessage]);

    return (
        <div className="flex-column flex-grow">
            <FormItem className="margin-horizontal-20" message={errorMessage} error={hasError} />
            <TableStatus
                className="padding-horizontal-20"
                dataProviderErrorMessage={criteriaTableModel.dataProviderErrorMessage}
                validationErrorMessage={criteriaTableModel.validationErrorMessage}
            />
            <CriteriaTable
                disabled={disabled}
                useMacroMePickerProvider={false}
                fieldsReady={criteriaTableModel.fieldsReady}
                onAddRow={() => criteriaTableModel.addRow()}
                onDeleteRow={(rowIndex, item) => criteriaTableModel.deleteRow(rowIndex)}
                onUpdateField={(rowIndex, item, fieldReferenceName) => criteriaTableModel.updateField(rowIndex, fieldReferenceName)}
                onUpdateOperator={(rowIndex, item, operator) => criteriaTableModel.updateOperator(rowIndex, operator)}
                onUpdateValue={(rowIndex, item, fieldValue) => criteriaTableModel.updateValue(rowIndex, fieldValue)}
                rows={criteriaTableModel.rows}
            />
        </div>
    );
}
